import React, { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import PerfectScrollbar from "react-perfect-scrollbar";
import MaskAndValid from "utils/maskAndValid";

import {
  Box,
  Grid,
  IconButton,
  Typography,
  Tooltip,
  InputBase,
  LinearProgress,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import PrintButton from "components/_refactor/button/print";
import componentStyles from "assets/theme/views/admin/react-bs-table";
import componentStylesNotification from "assets/theme/components/dropdowns/notifications-dropdown.js";
import componentNavbarStyles from "assets/theme/components/navbars/admin-navbar.js";
import Api from "lib/api/httpRequestHelper";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import ImagePlaceholder from "components/Image/ImagePlaceholder";
import OccurencesDialog from "components/Dialogs/OccurencesDialog";
import IconButtonLoader from "components/Buttons/IconButtonLoader";
import UtilsFncs from "utils/UtilsFncs";
import { utils, writeFileXLSX } from "xlsx";
import useLogs from "hooks/logs";

import {
  sortableField,
  columnPriceField,
  columnNumberField,
  columnBooleanField,
  columnTextField,
} from "utils/tables-utils";

import {
  CloudDownload,
  FileCopy,
  FindInPage,
  GetApp,
  LocalShipping,
  ExpandMore,
  ExpandLess,
  Search,
} from "@mui/icons-material";
import { IsMobile, IsMobile900 } from "utils/is-mobile";

const useStyles = makeStyles(componentStyles);
const useNavbarStyles = makeStyles(componentNavbarStyles);
const useStylesNotification = makeStyles(componentStylesNotification);

const columnModel = (
  truncarChaveNF,
  urlConsultaSefaz,
  classes,
  fetchOccurencesData,
  downloadXMLFile,
  downloadPDFFile,
) => [
  columnTextField({
    dataField: "GrupoEconomico",
    text: "Grupo Econômico",
  }),
  columnTextField({
    dataField: "RazaoSocial",
    text: "Razão Social",
  }),
  columnTextField({
    dataField: "Apelido",
    text: "Loja",
  }),
  columnTextField({
    dataField: "CnpjLojista",
    text: "CNPJ",
    formatter: (params) => {
      return MaskAndValid.maskCPNJCPF(params);
    },
  }),
  columnNumberField({
    dataField: "Numero",
    text: "Nr. NF",
  }),
  columnTextField({
    dataField: "DataEmissaoFormat",
    text: "Data Emissão",
    align: "center",
  }),
  columnTextField({
    dataField: "StatusNf",
    text: "Status",
    align: "center",
  }),
  columnTextField({
    dataField: "ValorLiquidoFormat",
    text: "Valor Liquido",
    align: "center",
  }),
  columnBooleanField({
    dataField: "Entregue",
    text: "Entregue",
  }),
  columnTextField({
    dataField: "DataPrevisaoEntregaFormat",
    text: "Data Previsão Entrega",
  }),
  columnNumberField({
    dataField: "NumeroProposta",
    text: "Nr. Proposta",
  }),
  columnNumberField({
    dataField: "NumeroPedido",
    text: "Nr. Pedido",
  }),
  columnTextField({
    dataField: "Marca",
    text: "Marca",
    align: "center",
  }),
  columnTextField({
    dataField: "CCLoja",
    text: "CC Loja",
    align: "center",
    headerAlign: "center",
  }),
  columnTextField({
    dataField: "NomeCondicaoPgto",
    text: "Condição Pagamento",
    align: "center",
  }),
  columnTextField({
    dataField: "ChaveNf",
    text: "Chave da NF",
    headerAlign: "center",
    align: "center",
    formatter: truncarChaveNF,
  }),
  columnBooleanField({
    dataField: "verOcorrencias",
    text: "Rastreio",
    headerAlign: "center",
    align: "center",
    formatter: (_, row) =>
      row.ChaveNf !== null &&
      row.ChaveNf > "" &&
      (row?.DataPrevisaoEntregaFormat !== "" ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButtonLoader
            title="Ver Ocorrências de Entrega da Nota"
            className={classes.margin}
            size="small"
            onClick={() => fetchOccurencesData(row)}
            icon={<LocalShipping />}
          />
        </div>
      ) : (
        <>
          {row?.NumeroProposta ? (
            <Tooltip title="Ver Ocorrências de Entrega da Nota" size="medium">
              <a
                style={{ color: "#525F7F" }}
                target="_blank"
                rel="noreferrer"
                href={row?.LinkRastreio}
              >
                <LocalShipping />
              </a>
            </Tooltip>
          ) : null}
        </>
      )),
  }),
  columnTextField({
    dataField: "consulta",
    text: "Consulta Sefaz",
    headerAlign: "center",
    align: "center",
    formatter: urlConsultaSefaz,
  }),
  columnTextField({
    dataField: "XmlNoS3",
    text: "XML",
    headerAlign: "center",
    align: "center",
    formatter: downloadXMLFile,
  }),
  columnTextField({
    dataField: "PdfNoS3",
    text: "PDF",
    headerAlign: "center",
    align: "center",
    formatter: downloadPDFFile,
  }),
];

function getInvoiceLineItems(row) {
  if (row?.ItensSellinNFSimples?.length > 0) {
    return row.ItensSellinNFSimples;
  }
  if (row?.ItensSellinNF?.length > 0) {
    return row.ItensSellinNF;
  }
  return [];
}

const modelTableItemDetail = [
  columnTextField({
    dataField: "UrlFoto",
    text: "Imagem",
    headerAlign: "center",
    align: "center",
    formatter: (_, row) => (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <ImagePlaceholder
          alt="Imagem do item do pedido"
          src={row.UrlFoto}
          width={64}
          widthOnError={40}
          imagePreviewTitle={`Produto ${row.IdProduto}`}
        />
      </div>
    ),
  }),
  columnTextField({
    dataField: "Sequencial",
    text: "Sequencial Item",
    align: "center",
  }),
  sortableField({
    dataField: "SituacaoItemPedido",
    text: "Situação Item Pedido",
    align: "center",
  }),
  sortableField({
    dataField: "LinhaProduto",
    text: "Linha Produto",
  }),
  sortableField({
    dataField: "ColecaoProduto",
    text: "Coleção Produto",
  }),
  sortableField({
    dataField: "IdProduto",
    text: "Id Produto",
    align: "center",
  }),
  sortableField({
    dataField: "DescricaoProduto",
    text: "Descrição Produto",
  }),
  sortableField({
    dataField: "CodCorProduto",
    text: "Cod Cor Produto",
    align: "center",
  }),
  sortableField({
    dataField: "TamanhoProduto",
    text: "Tamanho Produto",
  }),
  columnNumberField({
    dataField: "NumeroOd",
    text: "OD",
  }),
  columnNumberField({
    dataField: "QtdFaturada",
    text: "Qtd Faturada",
    align: "right",
  }),
  columnNumberField({
    dataField: "QtdDevolvida",
    text: "Qtd Devolvida",
    align: "right",
  }),
  columnPriceField({
    dataField: "PrecoUnitario",
    text: "Preço Unitário",
  }),
  columnPriceField({
    dataField: "ValorFinalItem",
    text: "Valor Liquido",
  }),
  sortableField({
    dataField: "NomeProduto",
    text: "Nome Produto",
  }),
];

const TabelaNotasFiscais = ({ dataTable }) => {
  const classes = {
    ...useStyles(),
    ...useNavbarStyles(),
    ...useStylesNotification(),
  };
  const snackbar = useSnackbar();
  const { setLocalStorageApiProblem, setLocalStorageActions } = useLogs();

  const searchRef = useRef(null);
  const searchInputRef = useRef(null);

  const [ocorrenciasNF, setOcorrenciasNF] = useState(null);

  const [searchValue, setSearchValue] = useState("");
  const [fullDataTable, setFullDataTable] = useState([]);
  const [searchItemsList, setSearchItemsList] = useState(null);
  const [searchingItems, setSearchingItems] = useState(false);

  const [expandedRows, setExpandedRows] = useState([]);
  const [pageConfig, setPageConfig] = useState({
    page: 1,
    pageSize: 25,
  });

  const isMobile = IsMobile();
  const isMobile900 = IsMobile900();

  const getFileInvoice = async (tipo, chave) => {
    return await Api.get(tipo, "invoice", "?chaveNf=" + chave, false)
      .then((value) => {
        const url =
          "data:application/octet-stream;base64," + value.Result.FileBase64;
        const downloadLink = document.createElement("a");
        downloadLink.href = url;
        downloadLink.target = "_blank";
        downloadLink.download = chave + "." + tipo;
        downloadLink.click();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const urlConsultaSefaz = useCallback(
    (cell, row) => {
      return (
        <div>
          {row.ChaveNf !== null && row.ChaveNf > "" ? (
            <IconButton
              title="Consultar Sefaz"
              className={classes.margin}
              href={
                "https://www.nfe.fazenda.gov.br/portal/consultaRecaptcha.aspx?tipoConsulta=resumo&nfe=" +
                row.ChaveNf
              }
              target="_blank"
              onClick={() => {
                setLocalStorageActions({
                  nomeTela: "Consulta de Notas Fiscais",
                  idMenu: "143",
                  acao: "Clicou em Consultar Sefaz",
                });
              }}
            >
              <FindInPage />
            </IconButton>
          ) : (
            ""
          )}
        </div>
      );
    },
    [classes.margin],
  );

  const truncarChaveNF = React.useCallback(
    (cell, row) => {
      return row.ChaveNf !== null && row.ChaveNf > "" ? (
        <div>
          <IconButtonLoader
            title="Copiar Chave NF"
            className={classes.margin}
            onClick={() => {
              UtilsFncs.CopyToClipboard(row.ChaveNf);
              setLocalStorageActions({
                nomeTela: "Consulta de Notas Fiscais",
                idMenu: "143",
                acao: "Clicou em Copiar Chave NF",
              });
            }}
            icon={<FileCopy />}
          />
        </div>
      ) : null;
    },
    [classes.margin],
  );

  const downloadXMLFile = React.useCallback(
    (cell, row) => {
      return row.XmlNoS3 ? (
        <div>
          <IconButtonLoader
            title="Download XML"
            onClick={(e) => {
              getFileInvoice("xml", row.ChaveNf, e);
              setLocalStorageActions({
                nomeTela: "Consulta de Notas Fiscais",
                idMenu: "143",
                acao: "Clicou em Download XML",
              });
            }}
            icon={<GetApp />}
          />
        </div>
      ) : null;
    },
    [classes.margin],
  );

  const downloadPDFFile = React.useCallback(
    (cell, row) => {
      return row.PdfNoS3 ? (
        <div>
          <IconButtonLoader
            title="Download PDF"
            size="small"
            onClick={(e) => {
              getFileInvoice("pdf", row.ChaveNf, e);
              setLocalStorageActions({
                nomeTela: "Consulta de Notas Fiscais",
                idMenu: "143",
                acao: "Clicou em Download PDF",
              });
            }}
            icon={<GetApp />}
          />
        </div>
      ) : null;
    },
    [classes.margin],
  );

  const fetchOccurencesData = useCallback(
    async (row) => {
      setLocalStorageActions({
        nomeTela: "Consulta de Notas Fiscais",
        idMenu: "143",
        acao: "Clicou em Ver Ocorrências de Entrega da Nota",
      });

      await Api.get(
        "tracking-occurrences",
        "sellinorder",
        "?chaveNF=" + row.ChaveNf,
        true,
      )
        .then((value) => {
          if (
            value?.ErrorMessage ===
            "HTTPERRO 422 Não foi possível encontrar ocorrências de entrega. Verifique os parâmetros informados."
          ) {
            snackbar.showMessage(
              "Não foi possível encontrar ocorrências de entrega para essa nota.",
              {
                severity: "info",
              },
            );
          } else if (!value || value.Result?.Ocorrencias.length === 0) {
            snackbar.showMessage(
              "Não foram encontradas ocorrências de entrega para essa nota.",
              {
                severity: "info",
              },
            );
          } else {
            setOcorrenciasNF({ ...row, ...value.Result });
          }
        })
        .catch((error) => {
          snackbar.showMessage(
            "Ocorreu um erro ao buscar por ocorrências de entrega para essa nota.",
            {
              severity: "error",
            },
          );
          console.log(error);
          setLocalStorageApiProblem({
            idMenuAccess: "143",
            screen: "Consulta de Notas Fiscais",
            problem: `Erro na chamada do endpoint  tracking-occurrences - ${error}`,
          });
        });
    },
    [snackbar],
  );

  const expandRow = {
    expanded: expandedRows,
    renderer: (row) => {
      const lineItems = getInvoiceLineItems(row);
      const detailRows = lineItems.map((item, idx) => ({
        ...item,
        __detailRowKey: `${row.Numero}-${idx}`,
      }));
      return lineItems.length > 0 ? (
        <Grid item xs={12}>
          <ToolkitProvider
            data={detailRows}
            keyField="__detailRowKey"
            columns={modelTableItemDetail}
          >
            {(props) => (
              <PerfectScrollbar>
                <BootstrapTable
                  {...props.baseProps}
                  bootstrap4={true}
                  bordered={false}
                  condensed={true}
                  striped={true}
                  hover={true}
                  style={{ overflow: "auto!important" }}
                />
              </PerfectScrollbar>
            )}
          </ToolkitProvider>
        </Grid>
      ) : (
        <Grid
          item
          xs={6}
          style={{
            display: "flex",
            justifyContent: isMobile900 ? "flex-start" : "center",
            flexDirection: isMobile ? "column" : "row",
            marginBottom: "-1rem",
          }}
        >
          <p
            style={{
              color: "#DF767D",
              fontWeight: "bold",
              fontSize: "0.8rem",
              marginBottom: isMobile && "-0.2rem",
            }}
          >
            Itens indisponíveis para consulta no momento.
          </p>
          <p
            style={{
              color: "#DF767D",
              fontWeight: "bold",
              fontSize: "0.8rem",
            }}
          >
            Tente novamente mais tarde.
          </p>
        </Grid>
      );
    },
    onExpand: (row, isExpanded, rowIndex) => {
      if (isExpanded) {
        setExpandedRows((indexes) => [...indexes, row.Numero]);
      } else {
        setExpandedRows((indexes) => indexes.filter((x) => x !== row.Numero));
      }
    },
    onlyOneExpanding: false,
    showExpandColumn: true,
    expandHeaderColumnRenderer: () => (
      <div
        onClick={(e) => {
          e.stopPropagation();
          if (expandedRows?.length > 0) {
            setExpandedRows([]);
          } else {
            setExpandedRows(
              dataTable
                .slice(pageConfig.page - 1, pageConfig.pageSize)
                .map((row) => row.Numero),
            );
          }
        }}
      >
        {expandedRows?.length > 0 ? <ExpandLess /> : <ExpandMore />}
      </div>
    ),
    expandColumnRenderer: ({ expanded }) => {
      if (expanded) {
        return <ExpandLess />;
      }
      return <ExpandMore />;
    },
  };

  function handleDownloadXML() {
    const data =
      searchItemsList?.length !== 0 && searchItemsList !== null
        ? searchItemsList
        : fullDataTable;

    const receipt = data.reduce((acc, item) => {
      const sellInItems = [...getInvoiceLineItems(item)];
      const nextHeader = { ...item };
      delete nextHeader.ItensSellinNFSimples;
      delete nextHeader.ItensSellinNF;
      return [...acc, nextHeader, ...sellInItems];
    }, []);

    const wb = utils.book_new();
    utils.book_append_sheet(wb, utils.json_to_sheet(receipt), "NFs");
    writeFileXLSX(wb, "NFs.xlsx");
  }

  const exportCmp =
    searchItemsList?.length !== 0 && searchItemsList !== null ? (
      <>
        <span>
          <Tooltip title="Exportar tudo">
            <IconButtonLoader
              onClick={() => handleDownloadXML()}
              icon={<CloudDownload />}
              size="small"
            />
          </Tooltip>
        </span>
      </>
    ) : (
      <>
        {fullDataTable?.length !== 0 ? (
          <>
            <span>
              <Tooltip title="Exportar tudo">
                <IconButtonLoader
                  onClick={() => handleDownloadXML()}
                  icon={<CloudDownload />}
                  size="small"
                />
              </Tooltip>
            </span>
          </>
        ) : null}
      </>
    );

  const handleClick = () => {
    if (searchValue !== "") {
      setSearchingItems(true);
      const listItens = [];
      fullDataTable?.map((item) => {
        if (
          item?.GrupoEconomico?.includes(searchValue) ||
          item?.RazaoSocial?.includes(searchValue?.toUpperCase()) ||
          item?.Apelido?.includes(searchValue?.toUpperCase()) ||
          item?.CnpjLojista?.includes(searchValue) ||
          item?.Numero?.toString()?.includes(searchValue?.toString()) ||
          item?.NumeroNf?.toString()?.includes(searchValue?.toString()) ||
          item?.DataEmissaoFormat?.includes(searchValue) ||
          item?.StatusNf?.includes(searchValue) ||
          item?.ValorLiquidoFormat?.includes(searchValue) ||
          item?.EntregueFormat?.includes(searchValue) ||
          item?.DataPrevisaoEntrega?.includes(searchValue) ||
          item?.NumeroProposta?.toString()?.includes(searchValue) ||
          item?.NumeroPedido?.toString()?.includes(searchValue?.toString()) ||
          item?.Marca?.includes(searchValue) ||
          item?.NomeCondicaoPgto?.includes(searchValue)
        ) {
          listItens.push(item);
        }
      });
      setSearchItemsList(listItens);
      setTimeout(() => {
        setSearchingItems(false);
      }, 500);
    }
  };

  const handleValueChange = useCallback((e) => {
    if (e.target.value === "") {
      setSearchItemsList(null);
    }
    setSearchValue(e.target.value);
    searchInputRef.current.focus();
  }, []);

  useEffect(() => {
    setFullDataTable(dataTable);
  }, [dataTable]);

  useEffect(() => {
    if (searchItemsList?.length === 0) {
      setTimeout(() => {
        setSearchItemsList(null);
      }, 2500);
    }
  }, [searchItemsList]);

  return (
    <>
      {searchingItems ? (
        <div style={{ marginTop: "1rem" }}>
          <span>Buscando dados..</span>
          <LinearProgress />
        </div>
      ) : (
        <ToolkitTable
          searched={Boolean(dataTable)}
          keyField="Numero"
          dataTable={searchItemsList ? searchItemsList : fullDataTable}
          columnModel={columnModel(
            truncarChaveNF,
            urlConsultaSefaz,
            classes,
            fetchOccurencesData,
            downloadXMLFile,
            downloadPDFFile,
          )}
          pagination={{
            options: [25, 50, 100],
            defaultOption: 25,
            totalSize: searchItemsList
              ? searchItemsList?.length
              : fullDataTable?.length,
            showTotal: true,
            onPageChange: (page, pageSize) => setPageConfig({ page, pageSize }),
          }}
          notFoundTitle="Sua pesquisa não encontrou Notas Fiscais correspondentes."
          bootstrapTableProps={{ expandRow }}
          CustomHeader={({ componentRefProps, selectRefProps }) => (
            <Grid
              container
              className="pl-4"
              style={{
                height: 60,
                padding: "0 24px 12px",
                marginBottom: isMobile && "1.5rem",
              }}
            >
              <Grid
                item
                xs={12}
                sm={6}
                style={{
                  display: "flex",
                  alignItems: isMobile ? "flex-start" : "center",
                  marginBottom: isMobile ? "1rem" : 0,
                  flexDirection: isMobile ? "column" : "row",
                }}
              >
                <Box
                  component={Typography}
                  variant="h3"
                  marginBottom="0!important"
                >
                  <Box component="span">Notas Fiscais</Box>
                </Box>
                <Box
                  display="flex"
                  ref={searchRef}
                  alignItems="center"
                  width="auto"
                  component={Grid}
                  className={classes.searchBox}
                  style={{ marginLeft: isMobile ? 0 : "1rem" }}
                >
                  <Tooltip title={"Pesquisar"}>
                    <Search
                      className={classes.searchIcon}
                      onClick={handleClick}
                      style={{ cursor: "pointer" }}
                      fontSize="large"
                    />
                  </Tooltip>
                  <InputBase
                    inputRef={searchInputRef}
                    placeholder="Faça a sua busca"
                    classes={{
                      input: classes.searchInput,
                    }}
                    value={searchValue}
                    onChange={handleValueChange}
                    autoComplete="off"
                    name="search"
                    autoFocus
                    inputProps={{
                      autoFocus: true,
                    }}
                  />
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: "center",
                  justifyContent: isMobile ? "flex-start" : "flex-end",
                  padding: 0,
                  marginBottom: "0.5rem!important",
                  marginTop: isMobile && "1rem",
                }}
              >
                <Box
                  justifyContent="flex-end"
                  alignItems="center"
                  display="flex"
                  flexWrap="wrap"
                  style={{
                    marginLeft: isMobile ? 0 : 15,
                    marginRight: 15,
                    cursor: "pointer",
                  }}
                >
                  {exportCmp}
                </Box>
                <Box
                  justifyContent="flex-end"
                  alignItems="center"
                  display="flex"
                  flexWrap="wrap"
                  style={{ marginLeft: 15, marginRight: 15, cursor: "pointer" }}
                >
                  <PrintButton
                    selectRef={selectRefProps}
                    componentRef={componentRefProps}
                    dataTable={
                      searchItemsList ? searchItemsList : fullDataTable
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        />
      )}

      {ocorrenciasNF ? (
        <OccurencesDialog
          data={ocorrenciasNF}
          handleClose={() => setOcorrenciasNF(null)}
          dialogTitle={`Ocorrências de Entrega da Nota ${
            ocorrenciasNF.NumeroNf
          } - ${ocorrenciasNF.SerieNf || ocorrenciasNF.Serie}`}
        />
      ) : null}
    </>
  );
};

TabelaNotasFiscais.propTypes = {
  dataTable: PropTypes.array,
};

export default TabelaNotasFiscais;
